import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, CheckIcon } from './icons';

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, onOutside]);
}

/**
 * Shared single-select popover dropdown — same trigger/panel/animation as
 * ReportsScreen's local TypeDropdown (button trigger, useClickOutside,
 * absolute-positioned panel with an opacity/scale transition, options as
 * buttons with a trailing checkmark on the selected one), extracted here so
 * every single-choice field in the app opens/closes the same way instead of
 * each screen rolling its own. Icons are optional per-option — omit them for
 * a plain text list (e.g. payout type) rather than forcing an icon.
 */
export default function Dropdown({ label, options, value, onChange, placeholder = 'Select…', error, className }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const selected = options.find((o) => o.value === value);

  return (
    <div className={clsx('relative', className)} ref={ref}>
      {label && <label className="block text-sm font-medium text-foreground-muted mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={clsx(
          'w-full flex items-center gap-3 rounded-xl bg-background-elevated border px-3.5 py-2.5 text-sm text-foreground transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
          error ? 'border-danger' : 'border-border hover:border-border-strong'
        )}
      >
        <span className="flex-1 text-left truncate">{selected?.label || placeholder}</span>
        <ChevronDownIcon className={clsx('h-4 w-4 text-foreground-subtle transition-transform duration-150', open && 'rotate-180')} />
      </button>

      <div
        role="listbox"
        className={clsx(
          'absolute z-20 mt-2 w-full origin-top rounded-xl border border-border bg-background-surface shadow-card-hover overflow-hidden transition-all duration-150',
          open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {options.map((o) => {
          const isSelected = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={clsx(
                'w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-background-elevated transition-colors',
                isSelected && 'bg-accent-soft/40'
              )}
            >
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">{o.label}</span>
                {o.description && <span className="block text-xs text-foreground-subtle mt-0.5">{o.description}</span>}
              </span>
              {isSelected && <CheckIcon className="h-4 w-4 text-accent shrink-0" />}
            </button>
          );
        })}
      </div>

      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
}
